import React, { useState } from 'react';
import { useNode } from "@craftjs/core";
import {
    FormLabel,
    TextField,
    Switch,
    FormControlLabel,
    Box,
    Button,
    IconButton,
    Select,
    MenuItem,
    Paper,
    Typography
} from "@mui/material";
import {
    Add as AddIcon,
    Delete as DeleteIcon
} from "@mui/icons-material";

export const TableSettings = () => {
    const {
        actions: { setProp },
        name,
        qwantumId,
        columns,
        rows,
        width,
        height,
        striped,
        bordered,
        hoverable,
        invoiceMode,
        currency,
        vatRate,
        language,
        allowRowManagement
    } = useNode((node) => ({
        name: node.data.props.name || "Table",
        qwantumId: node.data.props.qwantumId || "",
        columns: node.data.props.columns || [
            { title: "Column 1", width: "33%", defaultValue: "", editable: true },
            { title: "Column 2", width: "33%", defaultValue: "", editable: true },
            { title: "Column 3", width: "34%", defaultValue: "", editable: true }
        ],
        rows: node.data.props.rows || [
            ["Value 1", "Value 2", "Value 3"],
            ["Value 4", "Value 5", "Value 6"]
        ],
        width: node.data.props.width || "100%",
        height: node.data.props.height || "auto",
        striped: node.data.props.striped || false,
        bordered: node.data.props.bordered || false,
        hoverable: node.data.props.hoverable || false,
        invoiceMode: node.data.props.invoiceMode || false,
        currency: node.data.props.currency || "$",
        vatRate: node.data.props.vatRate || 20,
        language: node.data.props.language || "english",
        allowRowManagement: node.data.props.allowRowManagement !== undefined ? node.data.props.allowRowManagement : true
    }));

    const [localName, setLocalName] = useState(name);
    const [localWidth, setLocalWidth] = useState(width);
    const [localHeight, setLocalHeight] = useState(height);

    const addColumn = () => {
        const newColumns = [...columns, {
            title: `Column ${columns.length + 1}`,
            width: "auto",
            defaultValue: "",
            editable: true
        }];
        
        // Add a new cell to each existing row with the default value
        const expandedRows = rows.map(row => [
            ...row,
            ""  // Add empty value for the new column
        ]);
        
        setProp(props => {
            props.columns = newColumns;
            props.rows = expandedRows;
        });
    };

    const removeColumn = (index) => {
        if (columns.length > 1) {
            const newColumns = columns.filter((_, i) => i !== index);
            
            // Also trim the row data to match the new column count
            const trimmedRows = rows.map(row => 
                row.filter((_, colIndex) => colIndex !== index)
            );
            
            setProp(props => {
                props.columns = newColumns;
                props.rows = trimmedRows;
            });
        }
    };

    const updateColumn = (index, field, value) => {
        const newColumns = [...columns];
        newColumns[index] = { ...newColumns[index], [field]: value };
        setProp(props => props.columns = newColumns);
    };

    return (
        <div>
            {/* Basic Settings */}
            <Box mb={3}>
                <Typography variant="h6" gutterBottom>Basic Settings</Typography>
                
                <FormLabel>Component Name</FormLabel>
                <TextField
                    fullWidth
                    margin="normal"
                    value={localName}
                    onChange={(e) => setLocalName(e.target.value)}
                    onBlur={(e) => setProp(props => props.name = e.target.value)}
                    size="small"
                />

                <FormLabel>Quantum ID</FormLabel>
                <TextField
                    fullWidth
                    margin="normal"
                    value={qwantumId || ''}
                    onChange={(e) => setProp(props => props.qwantumId = e.target.value)}
                    size="small"
                    placeholder="Enter quantum ID for row data persistence"
                    helperText="Used to persist user data when component is in view mode"
                />

                <FormLabel>Width</FormLabel>
                <TextField
                    fullWidth
                    margin="normal"
                    value={localWidth}
                    onChange={(e) => setLocalWidth(e.target.value)}
                    onBlur={(e) => setProp(props => props.width = e.target.value)}
                    size="small"
                    placeholder="100%, 500px, auto"
                />

                <FormLabel>Height</FormLabel>
                <TextField
                    fullWidth
                    margin="normal"
                    value={localHeight}
                    onChange={(e) => setLocalHeight(e.target.value)}
                    onBlur={(e) => setProp(props => props.height = e.target.value)}
                    size="small"
                    placeholder="auto, 400px, 100vh"
                />
            </Box>

            {/* Invoice Mode */}
            <Box mb={3}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={invoiceMode}
                            onChange={(e) => setProp(props => props.invoiceMode = e.target.checked)}
                        />
                    }
                    label="Invoice Mode"
                />

                {invoiceMode && (
                    <Box mt={2} p={2} border={1} borderColor="grey.300" borderRadius={1}>
                        <Typography variant="subtitle2" gutterBottom>Invoice Settings</Typography>

                        <FormLabel>Language</FormLabel>
                        <Select
                            fullWidth
                            value={language}
                            onChange={(e) => setProp(props => props.language = e.target.value)}
                            size="small"
                            margin="normal"
                        >
                            <MenuItem value="english">English</MenuItem>
                            <MenuItem value="french">French</MenuItem>
                        </Select>

                        <FormLabel style={{ marginTop: '8px' }}>Currency</FormLabel>
                        <Select
                            fullWidth
                            value={currency}
                            onChange={(e) => setProp(props => props.currency = e.target.value)}
                            size="small"
                        >
                            <MenuItem value="$">Dollar ($)</MenuItem>
                            <MenuItem value="€">Euro (€)</MenuItem>
                        </Select>

                        <FormLabel style={{ marginTop: '8px' }}>Tax Rate (%)</FormLabel>
                        <TextField
                            fullWidth
                            type="number"
                            value={vatRate}
                            onChange={(e) => setProp(props => props.vatRate = parseFloat(e.target.value) || 0)}
                            size="small"
                            inputProps={{ min: 0, max: 100, step: 0.1 }}
                        />
                    </Box>
                )}
            </Box>

            {/* Table Behavior */}
            <Box mb={3}>
                <Typography variant="h6" gutterBottom>Table Behavior</Typography>
                
                <FormControlLabel
                    control={
                        <Switch
                            checked={allowRowManagement}
                            onChange={(e) => setProp(props => props.allowRowManagement = e.target.checked)}
                        />
                    }
                    label="Allow users to add/remove rows"
                />
            </Box>

            {/* Table Styling */}
            <Box mb={3}>
                <Typography variant="h6" gutterBottom>Table Styling</Typography>
                
                <FormControlLabel
                    control={
                        <Switch
                            checked={striped}
                            onChange={(e) => setProp(props => props.striped = e.target.checked)}
                        />
                    }
                    label="Striped rows"
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={bordered}
                            onChange={(e) => setProp(props => props.bordered = e.target.checked)}
                        />
                    }
                    label="Bordered"
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={hoverable}
                            onChange={(e) => setProp(props => props.hoverable = e.target.checked)}
                        />
                    }
                    label="Hoverable rows"
                />
            </Box>

            {/* Column Configuration */}
            {!invoiceMode && (
                <Box mb={3}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">Columns Configuration</Typography>
                        <Button
                            startIcon={<AddIcon />}
                            onClick={addColumn}
                            size="small"
                            variant="outlined"
                        >
                            Add Column
                        </Button>
                    </Box>

                    {columns.map((column, index) => (
                        <Paper key={index} elevation={1} style={{ padding: '16px', marginBottom: '12px' }}>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                <Typography variant="subtitle2">Column {index + 1}</Typography>
                                <IconButton
                                    onClick={() => removeColumn(index)}
                                    disabled={columns.length <= 1}
                                    size="small"
                                    color="error"
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Box>

                            <Box display="flex" flexDirection="column" gap={2}>
                                <TextField
                                    label="Column Title"
                                    value={column.title}
                                    onChange={(e) => updateColumn(index, 'title', e.target.value)}
                                    size="small"
                                    fullWidth
                                />

                                <TextField
                                    label="Width"
                                    value={column.width}
                                    onChange={(e) => updateColumn(index, 'width', e.target.value)}
                                    size="small"
                                    placeholder="33%, 200px, auto, 1fr"
                                    helperText="Use CSS units: %, px, auto, fr, em, rem"
                                />

                                <TextField
                                    label="Default Value"
                                    value={column.defaultValue}
                                    onChange={(e) => updateColumn(index, 'defaultValue', e.target.value)}
                                    size="small"
                                    fullWidth
                                    helperText="Default value for new rows"
                                />

                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={column.editable}
                                            onChange={(e) => updateColumn(index, 'editable', e.target.checked)}
                                        />
                                    }
                                    label="Editable"
                                />
                            </Box>
                        </Paper>
                    ))}
                </Box>
            )}

            {invoiceMode && (
                <Box mb={3} p={2} bgcolor="info.main" color="info.contrastText" borderRadius={1}>
                    <Typography variant="body2">
                        <strong>Invoice Mode Active:</strong> Columns are automatically configured for invoicing (Description, Quantity, Price, Amount)
                    </Typography>
                </Box>
            )}

            {/* Summary */}
            <Box mt={3} p={2} bgcolor="grey.100" borderRadius={1}>
                <Typography variant="body2" color="textSecondary">
                    <strong>Summary:</strong> {invoiceMode ? '4 invoice columns' : `${columns.length} custom columns`} configured
                </Typography>
            </Box>
        </div>
    );
};